import { useState, useEffect } from 'react';
import { Grid, Button, Group, FileInput, Loader, Modal, useMantineTheme, TextInput, Switch } from '@mantine/core';
import { createArticle, getArticles, updateArticle, deleteArticle, clearArticlesCache } from '../../services/articles.js';
import { FiEdit, FiTrash, FiPlus } from "react-icons/fi";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { uploadPhoto } from '../../services/files.js';
import { getBase64 } from '../../utils/filesToBase64.js';
import { replaceSrcInImgTags } from '../../utils/imageHandler.js';
import "./Articles.css"

function Articles() {
    const theme = useMantineTheme();
    const [articles, setArticles] = useState([]);
    const [selectedArticle, setSelectedArticle] = useState(null);
    const [articleData, setArticleData] = useState({ title: '', body: '', metaTags: {} });
    const [file, setFile] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [imagePreview, setImagePreview] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [notify, setNotify] = useState(true);

    useEffect(() => {
        fetchArticles();
    }, []);

    const fetchArticles = async () => {
        clearArticlesCache();
        const response = await getArticles({ limit: 10 });
        setArticles(response.articles);
    };

    const handleSelectArticle = (article) => {
        setSelectedArticle(article);
        setArticleData(article);
        setImagePreview(article.imageURL || '');
        setIsModalOpen(true);
    };

    const handleInputChange = (e) => {
        setArticleData({ ...articleData, [e.target.name]: e.target.value });
    };

    const handleBodyChange = async (e) => {
        let newValue = await replaceSrcInImgTags(e);
        setArticleData({ ...articleData, body: newValue });
    };

    const handleFileChange = (event) => {
        setFile(event);
        setImagePreview(URL.createObjectURL(event));
    };

    const handleSubmit = async () => {
        setIsLoading(true);

        if (!articleData.title.trim()) {
            alert("El título no debe estar vacío");
            setIsLoading(false);
            return;
        }

        if (!articleData.body.trim()) {
            alert("El artículo no debe estar vacío");
            setIsLoading(false);
            return;
        }

        let imageURL = imagePreview;

        if (file) {
            imageURL = (await uploadPhoto(await getBase64(file))).data;
        }

        try {
            if (selectedArticle) {
                await updateArticle({ articleId: selectedArticle._id, ...articleData, imageURL });
            } else {
                await createArticle({ ...articleData, imageURL, notify });
            }
            fetchArticles();
            setIsModalOpen(false);
            setFile(null);
            setArticleData({ title: '', body: '', metaTags: {} });
        } catch (error) {
            console.error('Error saving article:', error);
            alert('Error saving article');
        }

        setIsLoading(false);
    };

    const handleDelete = async (articleId) => {
        setIsLoading(true);
        await deleteArticle(articleId);
        fetchArticles();
        setIsLoading(false);
    };

    const handleNewArticle = () => {
        setSelectedArticle(null);
        setArticleData({ title: '', body: '', metaTags: {} });
        setImagePreview('');
        setIsModalOpen(true);
    };

    const modules = {
        toolbar: [
            [{ 'size': ['small', false, 'large', 'huge'] }],
            [{ 'header': [1, 2, false] }],
            ['bold', 'italic', 'underline', 'strike', 'blockquote'],
            [{ 'color': [] }, { 'background': [] }],
            [{ align: '' }, { align: 'center' }, { align: 'right' }],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'indent': '-1' }, { 'indent': '+1' }],
            ['link', 'image']
        ],
    };

    return (
        <div>
            <h1>Artículos</h1>
            <Button leftIcon={<FiPlus />} onClick={handleNewArticle} style={{ marginBottom: '20px' }}>
                Nuevo artículo
            </Button>

            <Grid>
                <Grid.Col span={12}>
                    {articles?.map(article => (
                        <div className='article-item' key={article._id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                            <div className='article-item__text'>{article.title}</div>
                            <Group spacing="xs">
                                <Button
                                    onClick={() => handleSelectArticle(article)}
                                    variant="outline"
                                    size='xs'
                                >
                                    <FiEdit />
                                </Button>
                                <Button
                                    onClick={() => handleDelete(article._id)}
                                    color="red"
                                    variant="outline"
                                    size='xs'
                                >
                                    <FiTrash />
                                </Button>
                            </Group>
                        </div>
                    ))}
                </Grid.Col>
            </Grid>
            <Modal
                opened={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                title={selectedArticle ? "Editar artículo" : "Nuevo artículo"}
                size="lg"
            >
                <TextInput
                    placeholder="Título"
                    name="title"
                    value={articleData.title}
                    onChange={handleInputChange}
                    mb="md"
                />
                {imagePreview && (
                    <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                        <img src={imagePreview} alt="Preview" style={{ maxWidth: '100%', maxHeight: '200px' }} />
                    </div>
                )}
                <FileInput
                    onChange={handleFileChange}
                    label="Subir imagen"
                    accept="image/*"
                    disabled={isLoading}
                    mb="md"
                />
                <ReactQuill theme="snow" modules={modules} value={articleData.body} onChange={handleBodyChange} />

                <Grid position="right" mt="md">
                    <Grid.Col span="auto">

                    </Grid.Col>
                    <Grid.Col span={3} >
                        {/* Conditionally render the "Notificar" switch */}
                        {!selectedArticle && (
                            <Switch
                                label="Notificar"
                                checked={notify}
                                onChange={(event) => setNotify(event.currentTarget.checked)}
                            />
                        )}
                    </Grid.Col>
                    <Grid.Col span={2}>

                        <Button onClick={handleSubmit} disabled={isLoading}>
                            {isLoading ? <Loader size="xs" /> : (selectedArticle ? 'Actualizar' : 'Crear')}
                        </Button>
                    </Grid.Col>
                </Grid>
            </Modal>

        </div>
    );
}

export default Articles;
